import * as config from '../app/config';
import { cognitoUser, logout } from '../features/auth/authSlice';
import { Microservices } from './AllowedMicroservices';
import type { Dispatch, AnyAction } from 'redux';
import type { CognitoUserSession } from 'amazon-cognito-identity-js';

// Shared fetch/token mechanics used by both ApiMiddleware (CALL_API) and
// ApiMiddlewareSequenced (CALL_API_SEQUENCED) - split out so each
// middleware only adds its own dispatch strategy on top of the same,
// single-source-of-truth HTTP plumbing, and so this plumbing can be unit
// tested directly instead of only indirectly through a whole middleware.

const DOMAIN = config.API_DOMAIN;
const PROTOCOL = config.API_PROTOCOL;

const COMPANY_PORT = config.COMPANY_PORT;
const LEARNING_PORT = config.LEARNING_PORT;
const EVALUATION_PORT = config.EVALUATION_PORT;
const KNOWLEDGEBASE_PORT = config.KNOWLEDGEBASE_PORT;
const CAMPAIGN_PORT = config.CAMPAIGN_PORT;
const MEDIA_PORT = config.MEDIA_PORT;
const ANALYTICS_PORT = config.ANALYTICS_PORT;

export const BASE_URL: Record<Microservices, string> = {
  [Microservices.COMPANY_SERVICE]: `${PROTOCOL}${DOMAIN}:${COMPANY_PORT}/v1`,
  [Microservices.FILES_SERVICE]: `${PROTOCOL}${DOMAIN}:${COMPANY_PORT}/v1`,
  [Microservices.USER_SERVICE]: `${PROTOCOL}${DOMAIN}:${COMPANY_PORT}/v1`,
  [Microservices.LEARNING_SERVICE]: `${PROTOCOL}${DOMAIN}:${LEARNING_PORT}/v1`,
  [Microservices.TOPIC_SERVICE]: `${PROTOCOL}${DOMAIN}:${LEARNING_PORT}/v1`,
  [Microservices.EVALUATION_SERVICE]: `${PROTOCOL}${DOMAIN}:${EVALUATION_PORT}/v1`,
  [Microservices.KNOWLEDGE_BASE_SERVICE]: `${PROTOCOL}${DOMAIN}:${KNOWLEDGEBASE_PORT}/v1`,
  [Microservices.CAMPAIGN_SERVICE]: `${PROTOCOL}${DOMAIN}:${CAMPAIGN_PORT}/v1`,
  [Microservices.MEDIA_SERVICE]: `${PROTOCOL}${DOMAIN}:${MEDIA_PORT}/v1`,
  [Microservices.X_ONE]: 'https://api.cognizant.x-one.cloud/api/',
  [Microservices.ANALYTICS_SERVICE]: `${PROTOCOL}${DOMAIN}:${ANALYTICS_PORT}/v1`,
};

export interface FetchConfigHeaders {
  'content-type'?: string;
  Accept?: string;
  Authorization?: string;
  [key: string]: string | undefined;
}

export interface FetchConfig {
  method?: string;
  headers: FetchConfigHeaders;
  body?: any;
}

export const addTokenIfIsAuthenticated = ({
  authenticated,
  token,
  config,
  endpoint,
}: {
  authenticated?: boolean;
  token?: string | null;
  config: FetchConfig;
  endpoint: string;
}): FetchConfig => {
  if (!authenticated) {
    return config;
  }
  if (token) {
    config.headers['Authorization'] = token;
    return config;
  }
  throw new Error(`NO_TOKEN_SAVED, ${endpoint}`);
};

export const addBody = (data: any, configWithHeaders: FetchConfig): FetchConfig => {
  if (!data) {
    return configWithHeaders;
  }
  if (configWithHeaders.headers['content-type'] === 'multipart/form-data') {
    delete configWithHeaders.headers['content-type'];
    delete configWithHeaders.headers['Accept'];
    return {
      ...configWithHeaders,
      body: data,
    };
  }
  if (configWithHeaders.headers['content-type'] === 'application/x-www-form-urlencoded') {
    return {
      ...configWithHeaders,
      body: data,
    };
  }
  return {
    ...configWithHeaders,
    body: JSON.stringify(data),
  };
};

// The real CognitoUserSession type (amazon-cognito-identity-js) has no
// `.accessToken` property - only `.getAccessToken()` - but that's what a
// refreshSession callback's session actually carries at runtime here, so
// this one stays a narrow local shape rather than borrowing (and lying
// about) the SDK's own session type.
interface RefreshedSession {
  accessToken: { jwtToken: string };
}

export const updateToken = (next: Dispatch<AnyAction>): Promise<string | undefined> => {
  return new Promise((resolve, reject) => {
    cognitoUser?.getSession(async (_err: Error | null, session: CognitoUserSession | null) => {
      if (session) {
        const refreshToken = session.getRefreshToken();
        cognitoUser?.refreshSession(refreshToken, async (_err: any, session: RefreshedSession | null) => {
          if (session) {
            return resolve(session.accessToken.jwtToken);
          }
          await next(logout());
          reject();
        });
        return;
      }
      await next(logout());
      reject();
    });
  });
};

export const generateMessage = (
  url: string,
  config: FetchConfig,
  textMessage?: string,
  status?: number | string,
  data?: any
): Record<string, any> => {
  const { method, headers } = config;
  const message: Record<string, any> = {
    title: 'API_ERROR',
    url,
    method,
    ...headers,
  };
  if (textMessage) {
    message.textMessage = textMessage;
  }
  if (status) {
    message.status = status;
  }
  if (data) {
    message.submittedData = JSON.stringify(data);
  }
  return message;
};

export async function callApi({
  endpoint,
  authenticated,
  microservice,
  method,
  data,
  token,
  next,
  contentType = 'application/json',
  accept = 'application/json',
}: {
  endpoint: string;
  authenticated?: boolean;
  microservice: Microservices;
  method?: string;
  data?: any;
  token?: string | null;
  next: Dispatch<AnyAction>;
  contentType?: string;
  accept?: string;
}): Promise<any> {
  const headers: FetchConfigHeaders = {
    'content-type': contentType,
    Accept: accept,
  };
  const configWithHeaders = addTokenIfIsAuthenticated({
    authenticated,
    token,
    config: { method, headers },
    endpoint,
  });
  const config = addBody(data, configWithHeaders);
  let response: Response | null = null;
  const url = BASE_URL[microservice] + endpoint;
  try {
    response = await fetch(url, config as RequestInit);
  } catch (e) {
    const errorMessage = generateMessage(
      url,
      config,
      e.message,
      response ? (response as Response).status : 'NET_ERROR',
      data
    );
    return Promise.reject({ data: e.message, errorMessage });
  }
  if (response.status === 401) {
    // TODO: change to cognito
    const newToken = (await updateToken(next)) || '';
    if (newToken) {
      config.headers['Authorization'] = newToken;
      try {
        response = await fetch(BASE_URL[microservice] + endpoint, config as RequestInit);
      } catch (e) {
        const errorMessage = generateMessage(url, config, e.message, response.status, data);
        return Promise.reject({
          data: e.message,
          status: response.status,
          errorMessage,
        });
      }
    }
  }

  if (!response.ok) {
    const json = await response.json();
    const errorMessage = generateMessage(url, config, JSON.stringify(json), response.status, data);
    return Promise.reject({
      status: response.status,
      data: json,
      errorMessage,
    });
  }
  let json = null;
  if (response.status === 204) {
    return '';
  }
  try {
    if (accept !== 'application/json' && (response as any).blob) {
      return await (response as any).blob();
    }
    json = await response.json();
  } catch (e) {
    const errorMessage = generateMessage(url, config, e.message, undefined, response);
    return Promise.reject({ data: e.message, errorMessage });
  }
  return json;
}

export const getToken = (authenticated?: boolean): string | null => {
  if (!authenticated || !cognitoUser) {
    return null;
  }
  if (!cognitoUser.getSignInUserSession()) {
    return null;
  }
  return cognitoUser.getSignInUserSession()!.getAccessToken().getJwtToken();
};

export interface StoreLike {
  getState: () => { security?: { refreshToken?: string } };
}

export const getRefreshToken = (store: StoreLike): string | null => {
  const { security } = store.getState();
  if (!security) {
    return null;
  }
  return security.refreshToken || null;
};

export interface ActionWithSelector {
  actionOrCreator: ((arg?: any) => AnyAction) | AnyAction;
  selector?: (arg: any) => any;
}

export interface CallApiTypes {
  requestType: AnyAction | (() => AnyAction) | string;
  successTypes: ActionWithSelector | ActionWithSelector[];
  errorTypes: ActionWithSelector | ActionWithSelector[];
}

// Shape of the payload a CALL_API/CALL_API_SEQUENCED action carries (see
// apiActionCreator.ts / apiActionCreatorSequenced.ts, which build it).
// `sequenceKey` is only meaningful for CALL_API_SEQUENCED.
export interface CallApiPayload {
  endpoint: string;
  types: CallApiTypes;
  authenticated?: boolean;
  method?: string;
  data?: any;
  microservice: Microservices;
  contentType?: string;
  accept?: string;
  addOriginalAuthorization?: boolean;
  customToken?: string;
  sequenceKey?: string;
}

export const executeActionsResponse = (
  next: Dispatch<AnyAction>,
  response: any,
  typeObjs: ActionWithSelector | ActionWithSelector[]
): any => {
  const actionTypesObjects = ([] as ActionWithSelector[]).concat(typeObjs);
  actionTypesObjects.forEach((actionTypeObj) => {
    const actionIntend = actionTypeObj.actionOrCreator;
    const selector = actionTypeObj.selector;
    if (typeof actionIntend === 'function') {
      const actionCreator = actionIntend;
      if (typeof selector === 'function') {
        return next(actionCreator(selector(response)));
      } else {
        return next(actionCreator(response));
      }
    } else if (typeof actionIntend === 'object') {
      const action = actionIntend;
      return next(action);
    }

    return next({
      type: 'unknown-action',
    });
  });
};

export const executeActionsError = (
  next: Dispatch<AnyAction>,
  error: any,
  typesObjs: ActionWithSelector | ActionWithSelector[]
): any => {
  const actionTypesObjects = ([] as ActionWithSelector[]).concat(typesObjs);
  actionTypesObjects.forEach((actionTypeObj) => {
    const actionIntend = actionTypeObj.actionOrCreator;
    if (typeof actionIntend === 'function') {
      const selector = actionTypeObj.selector;
      const actionCreator = actionIntend;
      if (typeof selector === 'function') {
        return next(actionCreator(selector(error)));
      } else {
        return next(actionCreator());
      }
    } else if (typeof actionIntend === 'object') {
      const action = actionIntend;
      return next(action);
    }

    return next({
      type: 'unknown-action',
    });
  });
};
